import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, TextField, Tooltip, IconButton } from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useDrop, useDrag } from 'react-dnd';

import { Nullable } from '~globals/types/commons';

import {
  RoadmapColumnsFieldSortableItemDragData,
  RoadmapColumnsFieldSortableItemProps,
} from './types';
import { ITEM_TYPES_DND } from './utils';

const RoadmapColumnsFieldSortableItem = ({
  columnId,
  value,
  findColumnIndex,
  onMove,
  onRemove,
  disabled = false,
}: RoadmapColumnsFieldSortableItemProps): ReactElement => {
  const originalIndex = useMemo(
    () => findColumnIndex(columnId),
    [columnId, findColumnIndex],
  );

  const [{ isDragging }, dragConnect, dragPreviewConnect] = useDrag(
    () => ({
      type: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
      item: { columnId, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const {
          columnId: droppedColumnId,
          originalIndex: dropperOriginalIndex,
        } = item;

        if (!monitor.didDrop()) {
          onMove(droppedColumnId, dropperOriginalIndex);
        }
      },
    }),
    [columnId, onMove],
  );

  const [, dropConnect] = useDrop(
    () => ({
      accept: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
      hover(item: RoadmapColumnsFieldSortableItemDragData) {
        const { columnId: draggedColumnId } = item;

        if (draggedColumnId !== columnId) {
          onMove(draggedColumnId, originalIndex);
        }
      },
    }),
    [columnId, onMove, originalIndex],
  );

  const connectRef = useCallback(
    (node: Nullable<HTMLDivElement>) =>
      !disabled ? dragPreviewConnect(dropConnect(node)) : node,
    [disabled, dragPreviewConnect, dropConnect],
  );

  return (
    <Stack
      ref={connectRef}
      sx={{ opacity: isDragging ? 0 : 1 }}
      spacing={1}
      direction="row"
      alignItems="center"
    >
      <IconButton
        aria-label="order column"
        size="small"
        disabled={disabled}
        ref={dragConnect}
        sx={{
          '&:not(:disabled)': {
            cursor: 'move',
          },
        }}
        disableFocusRipple
        disableRipple
        disableTouchRipple
      >
        <DragIcon />
      </IconButton>

      <TextField
        defaultValue={value}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        disabled
      />

      <Tooltip title="Eliminar columna" arrow placement="right">
        <span>
          <IconButton
            aria-label="Delete column"
            onClick={() => onRemove(originalIndex)}
            color="error"
            size="small"
            disabled={disabled || isDragging}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default RoadmapColumnsFieldSortableItem;
